/**
 * VRG-696 — the master pipeline's VIEW state (URL) and its mapping onto the shared SankeyChart.
 * The arithmetic is in `@repo/types` gtm/master-pipeline and runs on the API; this file only
 * reads the URL, formats, and chooses what the chart shows.
 *
 * Everything a viewer chose lives in the URL, so a sliced view is a link someone can send:
 *   ?range=30d&by=channel&measure=revenue&period=mrr&industry=Handbags&country=US&value_min=10000
 *
 * VRG-700 — the period defaults to ARR (`period` is written only for MRR), and the money
 * filters follow the switch on SCREEN while the URL stays canonical ANNUAL USD: `value_min=10000`
 * means $10K a year whether the inputs show it as $10K ARR or $833.33 MRR, so changing the
 * switch never changes the slice and an old link keeps its meaning. The conversion is
 * `periodBoundForInput` / `periodBoundFromInput` in @repo/types (guarded).
 */
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  DEFAULT_REVENUE_PERIOD,
  isSmallSample,
  MASTER_FACETS,
  parseMasterAttribution,
  parseRevenuePeriod,
  SMALL_SAMPLE_N,
  type RevenuePeriod,
  type MasterAttribution,
  type MasterFacet,
  type MasterFlow,
  type MasterMeasure,
  type MasterPipelineFilters,
} from '@repo/types'

import type { SankeyChartLink, SankeyChartNode } from 'components/analytics/SankeyChart'
import type { ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'

import { isPipelineRange, moneyShort, type PipelineRange } from '../GtmSalesPage/dealsModel'
import { rangeForPreset } from '../InternalAnalyticsPage/websiteAnalyticsModel'

export type MasterMeasureMode = 'count' | 'revenue'
export type { RevenuePeriod }
export { DEFAULT_REVENUE_PERIOD }

/** The MRR/ARR switch, shared by GTM → Channels and Sales → Pipeline (ARR is the default). */
export const PERIOD_OPTIONS: readonly ViewToggleOption<RevenuePeriod>[] = [
  { value: 'mrr', label: 'MRR' },
  { value: 'arr', label: 'ARR' },
]
/** VRG-701 adds `sub_source`: each channel split by its sub-source. */
export type SourceGranularity = 'group' | 'channel' | 'sub_source'

export const MONEY_PARAMS = ['value_min', 'value_max', 'actual_min', 'actual_max'] as const
export type MoneyParam = (typeof MONEY_PARAMS)[number]

export interface MasterView {
  preset: PipelineRange
  by: SourceGranularity
  measure: MasterMeasureMode
  period: RevenuePeriod
  /** VRG-701 — each deal under its own source (default), or its company's first touch. */
  attribution: MasterAttribution
  filters: MasterPipelineFilters
  /** How many facet / value filters are set (the date range is not counted). */
  activeFilters: number
}

const num = (v: string | null): number | null => {
  if (v === null || v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? n : null
}

/** The view from the URL, plus setters that keep everything else in it. */
export const useMasterView = () => {
  const [params, setParams] = useSearchParams()
  const view = useMemo<MasterView>(() => {
    const preset: PipelineRange = isPipelineRange(params.get('range')) ? (params.get('range') as PipelineRange) : 'all'
    const range = preset === 'all' ? null : rangeForPreset(preset)
    const attribution = parseMasterAttribution(params.get('attribution'))
    const filters: MasterPipelineFilters = { range: range ? { since: range.since, until: range.until } : null, attribution }
    let active = 0
    for (const f of MASTER_FACETS) {
      const values = params.getAll(f).filter(Boolean)
      filters[f] = values.length ? values : null
      if (values.length) active += 1
    }
    for (const k of MONEY_PARAMS) {
      filters[k] = num(params.get(k))
      if (filters[k] !== null) active += 1
    }
    return {
      preset,
      by: params.get('by') === 'channel' ? 'channel' : params.get('by') === 'sub_source' ? 'sub_source' : 'group',
      measure: params.get('measure') === 'revenue' ? 'revenue' : 'count',
      period: parseRevenuePeriod(params.get('period')),
      attribution,
      filters,
      activeFilters: active,
    }
  }, [params])

  const update = (fn: (p: URLSearchParams) => void) => {
    const p = new URLSearchParams(params)
    fn(p)
    setParams(p, { replace: true })
  }
  return {
    view,
    setParam: (key: string, value: string | null) =>
      update((p) => {
        if (value === null) p.delete(key)
        else p.set(key, value)
      }),
    /** Several params in ONE URL update (two setParam calls in a row would each start from the old URL). */
    setParams: (entries: Record<string, string | null>) =>
      update((p) => {
        for (const [key, value] of Object.entries(entries)) {
          if (value === null) p.delete(key)
          else p.set(key, value)
        }
      }),
    setFacet: (facet: MasterFacet, values: readonly string[]) =>
      update((p) => {
        p.delete(facet)
        for (const v of values) p.append(facet, v)
      }),
    clearFilters: () =>
      update((p) => {
        for (const f of MASTER_FACETS) p.delete(f)
        for (const k of MONEY_PARAMS) p.delete(k)
      }),
  }
}

/** Annual USD → the figure in the chosen period. */
export const inPeriod = (arr: number, period: RevenuePeriod): number => (period === 'mrr' ? arr / 12 : arr)

export const periodLabel = (period: RevenuePeriod): string => (period === 'mrr' ? 'MRR' : 'ARR')

export const pct = (part: number, whole: number): string => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—')

const splitLine = (m: MasterMeasure, period: RevenuePeriod): string =>
  `Actual ${moneyShort(inPeriod(m.actual_arr, period))} · predicted ${moneyShort(inPeriod(m.predicted_arr, period))} ${periodLabel(period)}`

const withoutLine = (m: MasterMeasure): string | null =>
  m.without_value > 0 ? `${m.without_value} ${m.without_value === 1 ? 'deal' : 'deals'} without a value (0 wide in Revenue)` : null

/**
 * The flow as SankeyChart nodes and links. Count mode weighs by n; Revenue mode by each deal's
 * one value, in the chosen period. Every node and band carries its n (tooltip, and the node's
 * label caption), its share, and — in Revenue mode — its actual vs predicted split. Shares under
 * n = SMALL_SAMPLE_N render greyed by the chart (`smallSampleBelow`), never hidden.
 */
export const masterChart = (
  flow: MasterFlow,
  total: MasterMeasure,
  measure: MasterMeasureMode,
  period: RevenuePeriod,
): { nodes: SankeyChartNode[]; links: SankeyChartLink[] } => {
  const weight = (m: MasterMeasure) => (measure === 'count' ? m.n : inPeriod(m.arr, period))
  const totalWeight = weight(total)
  const detail = (m: MasterMeasure): string[] =>
    [measure === 'revenue' ? splitLine(m, period) : null, withoutLine(m)].filter((x): x is string => !!x)
  return {
    nodes: flow.nodes.map((n) => ({
      id: n.id,
      label: n.label,
      tone:
        n.column === 'source'
          ? 'source'
          : n.id === 'outcome:won'
            ? 'success'
            : n.id === 'outcome:lost'
              ? 'end'
              : 'stage',
      n: n.n,
      caption: measure === 'count' ? pct(n.n, total.n) : `n ${n.n} · ${pct(weight(n), totalWeight)}`,
      detail: detail(n),
    })),
    links: flow.links.map((l) => ({ source: l.source, target: l.target, value: weight(l), n: l.n, detail: detail(l) })),
  }
}

export const SMALL_SAMPLE = SMALL_SAMPLE_N
export { isSmallSample }
